import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import {
  InputField, TooltipWidget, ErrorMessage,
} from 'anf-core-react';
import { DEFAULT_ICON, formatCardNumber } from './helpers/formatCard';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import { isValidCvv } from './helpers/cardValidations';

const DEFAULT_MAX_LENGTH = 3;

export default function CVVField({ index, hasValidationError, cardBrand: cardBrandProp }) {
  const [isToolTipOpen, setIsToolTipOpen] = useState(false);
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const {
    control, setError, trigger, watch,
  } = useFormContext();

  const cvvFieldName = `creditCard[${index}].cvv`;
  const cardNumberFieldName = `creditCard[${index}].cardNumber`;
  const cardNumberFieldValue = watch(cardNumberFieldName);

  const { paymentConfig: { creditCard: creditCardConfig } = {} } = checkoutPageState || {};
  const cardTypesList = creditCardConfig?.creditCardTypes;
  const label = creditCardConfig?.securityCodeLabel?.value || '';
  const cvvErrorMessage = creditCardConfig?.cvvErrorMessage?.value || '';
  const tooltipMessage = creditCardConfig?.securityCodeMessage?.value || '';
  const tooltipImage = creditCardConfig?.securityCodeImage || '';
  const tooltipButtonLabel = creditCardConfig?.tooltipButtonLabel?.value || '';

  useEffect(() => {
    if (hasValidationError) {
      setError(cvvFieldName, {
        type: 'manual',
      });
    }
  }, [cvvFieldName, hasValidationError, setError]);

  const maxLength = useMemo(() => {
    let cardBrand = '021';
    if (cardBrandProp) {
      cardBrand = cardBrandProp;
    } else if (cardNumberFieldValue) {
      const formattedCardNumber = formatCardNumber(cardNumberFieldValue, cardTypesList);
      cardBrand = formattedCardNumber.type ? formattedCardNumber.type : DEFAULT_ICON;
    }
    if (cardBrand && String(cardBrand).match(/(amex)|(003)/)) {
      return 4;
    }
    return DEFAULT_MAX_LENGTH;
  }, [cardNumberFieldValue, cardTypesList, cardBrandProp]);

  const validCVVFn = useCallback(
    (value) => ((value.length === maxLength) && isValidCvv(value)) || cvvErrorMessage,
    [cvvErrorMessage, maxLength],
  );

  return (
    <Controller
      name={cvvFieldName}
      control={control}
      defaultValue=""
      rules={{
        required: cvvErrorMessage,
        validate: validCVVFn,
      }}
      render={({ field, fieldState }) => (
        <InputField
          label={label}
          id={`cvv-field-${index}`}
          onBlur={trigger}
          type="tel"
          maxLength={maxLength}
          isInvalid={Boolean(fieldState.error)}
          autoComplete=""
          classList="cvv-field"
          inputProps={{
            'data-validationrule': 'cvv',
            'data-testid': `cvv-field-${index}`,
          }}
          {...field}
        >
          <TooltipWidget
            buttonLabelText={tooltipButtonLabel}
            contentID={`cvv-tooltip-${index}`}
            contentPosition="top-right"
            isOpen={isToolTipOpen}
            onFocus={() => setIsToolTipOpen(true)}
            onBlur={() => setIsToolTipOpen(false)}
            onClick={() => setIsToolTipOpen(!isToolTipOpen)}
            onKeyDown={() => setIsToolTipOpen(!isToolTipOpen)}
            onKeyUp={() => {}}
          >
            <img src={tooltipImage} alt={tooltipMessage} />
          </TooltipWidget>
          {fieldState.error ? (
            <ErrorMessage id="cvv-error-message">
              {fieldState.error.message}
            </ErrorMessage>
          ) : null}
        </InputField>
      )}
    />
  );
}

CVVField.defaultProps = {
  index: 0,
  cardBrand: '',
};

CVVField.propTypes = {
  index: PropTypes.number,
  cardBrand: PropTypes.string,
  hasValidationError: PropTypes.bool.isRequired,
};
